import React, { useMemo, useState } from 'react';
import { normalizeGalleryImages } from '../../utils/imageLightbox';
import ImageLightboxModal from './ImageLightboxModal';

const ImageGallery = ({ images = [], title = 'Images' }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const slides = useMemo(() => normalizeGalleryImages(images, title), [images, title]);

  if (!slides.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
        <div className="w-full h-64 bg-gray-200 flex items-center justify-center rounded-lg">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <p className="text-gray-500">No Images Available</p>
          </div>
        </div>
      </div>
    );
  }

  const openModal = (index) => setOpenIndex(index);

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {title} ({slides.length})
        </h2>

        <div className="mb-4">
          <button
            type="button"
            className="relative w-full cursor-pointer group overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
            onClick={() => openModal(0)}
            style={{ aspectRatio: '16/9' }}
            aria-label="View gallery"
          >
            <img
              src={slides[0].url}
              alt={slides[0].alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-opacity duration-200 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center">
                <svg className="w-12 h-12 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
                <p className="text-white text-sm mt-2">Click to view gallery</p>
              </div>
            </div>
            {slides.length > 1 && (
              <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                +{slides.length - 1} more
              </div>
            )}
          </button>
        </div>

        {slides.length > 1 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {slides.slice(1, 7).map((slide, thumbOffset) => {
              const index = thumbOffset + 1;
              return (
                <button
                  key={slide.id}
                  type="button"
                  className="relative cursor-pointer group aspect-square overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
                  onClick={() => openModal(index)}
                  aria-label={`View ${slide.alt}`}
                >
                  <img
                    src={slide.url}
                    alt={slide.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-opacity duration-200 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </button>
              );
            })}
            {slides.length > 7 && (
              <button
                type="button"
                className="relative cursor-pointer group bg-gray-200 rounded-lg flex items-center justify-center aspect-square focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
                onClick={() => openModal(7)}
                aria-label="View more images"
              >
                <div className="text-center">
                  <svg className="w-6 h-6 text-gray-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <p className="text-xs text-gray-500">+{slides.length - 7} more</p>
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      {openIndex !== null && (
        <ImageLightboxModal
          isOpen
          images={slides}
          startIndex={openIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </>
  );
};

export default ImageGallery;
